import React, { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ClipboardList,
  RefreshCcw,
  Loader2,
  AlertTriangle,
  Info,
  Building2,
  CheckCircle2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDateTime } from "@/lib/api";

const SIGNAL_BADGE = {
  BUY: "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/40",
  WATCH: "bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40",
  HOLD: "bg-muted text-muted-foreground border",
  NO_DATA: "bg-muted/50 text-muted-foreground border",
};

const stripBullet = (s) => (s || "").replace(/^[\s✅⚠•·\-–—]+/, "").trim();

const InvestmentRationalePanel = ({
  cseSymbol,
  compositeScore,
  className,
}) => {
  const [rationale, setRationale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const load = async (force = false) => {
    if (!cseSymbol) return;
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const url = force
        ? `/rationale/${cseSymbol}?refresh=true`
        : `/rationale/${cseSymbol}`;
      const data = await apiGet(url);
      setRationale(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load rationale");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cseSymbol]);

  // Detect staleness — score moved >10 since the rationale was generated
  const currentScore = compositeScore?.final_score ?? null;
  const generatedScore = rationale?.score_at_generation ?? null;
  const stale =
    rationale &&
    currentScore != null &&
    generatedScore != null &&
    Math.abs(currentScore - generatedScore) > 10;

  // Special banners (Phase 7 Stage 6)
  const showSpecialDiv = !!compositeScore?.special_dividend_detected;
  const showLumpy = !!compositeScore?.layer2_lumpy_earnings;
  const showTrust = !!compositeScore?.is_investment_trust;

  // Loading skeleton
  if (loading && !rationale) {
    return (
      <Card className={cn("border bg-card", className)} data-testid="rationale-loading">
        <CardContent className="py-12 flex flex-col items-center justify-center gap-3">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Generating investment rationale…
          </p>
        </CardContent>
      </Card>
    );
  }

  // Hard error → empty state
  if (error && !rationale) {
    return (
      <Card className={cn("border bg-card", className)} data-testid="rationale-error">
        <CardContent className="py-8 flex flex-col items-center gap-2 text-center">
          <AlertTriangle className="h-5 w-5 text-warning" />
          <p className="text-sm text-muted-foreground max-w-md">{error}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(true)}
            className="mt-2 gap-2"
            data-testid="rationale-retry-button"
          >
            <RefreshCcw className="h-3.5 w-3.5" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!rationale) return null;

  const why = rationale.why_signal || [];
  const concerns = rationale.concerns || [];
  const watch = rationale.what_to_watch || [];
  const bottomLine = rationale.bottom_line;
  const compact = rationale.rationale_compact;
  const signal = rationale.signal_at_generation || compositeScore?.signal || "—";
  const scoreAtGen = rationale.score_at_generation;

  return (
    <div className={cn("space-y-3", className)} data-testid="rationale-panel">
      {/* Special-flag banners — appear ABOVE the rationale per spec */}
      {showSpecialDiv && (
        <Card className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" data-testid="banner-special-dividend">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="text-xs leading-snug">
              <div className="font-semibold text-warning">Special Dividend Warning</div>
              <p className="text-foreground/80 mt-0.5">
                The high yield may include a one-off special dividend payment. Regular yield is lower. Yield score capped at sector median for scoring.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {showLumpy && (
        <Card className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" data-testid="banner-lumpy">
          <CardContent className="py-3 flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <div className="text-xs leading-snug">
              <div className="font-semibold text-warning">Earnings Spike Detected</div>
              <p className="text-foreground/80 mt-0.5">
                Latest EPS is significantly above historical average. Growth score uses median to avoid one-off inflation.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
      {showTrust && (
        <Card className="border-primary/40 bg-primary/5" data-testid="banner-trust">
          <CardContent className="py-3 flex items-start gap-3">
            <Building2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <div className="text-xs leading-snug">
              <div className="font-semibold text-primary">Investment Trust</div>
              <p className="text-foreground/80 mt-0.5">
                PE and Graham Number valuation models are not applied. Scored on yield, financial health, and NAV discount instead.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {stale && (
        <div
          className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs flex items-center justify-between gap-2"
          data-testid="rationale-stale-banner"
        >
          <span className="text-warning">
            ⚠ Score has changed since this analysis was generated
            (was {generatedScore}, now {currentScore}).
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => load(true)}
            disabled={refreshing}
            className="gap-1.5 h-7 text-[11px]"
            data-testid="rationale-refresh-stale"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCcw className="h-3 w-3" />}
            Refresh
          </Button>
        </div>
      )}

      <Card className="border bg-card overflow-hidden">
        <CardHeader className="pb-3 border-b bg-muted/20">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="space-y-1.5">
              <div className="font-display text-base flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-primary" />
                Investment Rationale
              </div>
              <div className="flex items-center gap-2 flex-wrap text-xs">
                <span className="font-mono text-muted-foreground">{cseSymbol}</span>
                <Badge variant="outline" className={cn("text-[10px] uppercase tracking-wide", SIGNAL_BADGE[signal] || SIGNAL_BADGE.HOLD)}>
                  {signal} signal
                </Badge>
                {scoreAtGen != null && (
                  <span className="text-muted-foreground">
                    Score <span className="font-mono text-foreground">{scoreAtGen}/100</span>
                  </span>
                )}
                {rationale.generated_at && (
                  <span className="text-muted-foreground">
                    · Generated {formatDateTime(rationale.generated_at)}
                  </span>
                )}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => load(true)}
              disabled={refreshing}
              className="gap-1.5"
              data-testid="rationale-refresh-button"
            >
              {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Refresh
            </Button>
          </div>
        </CardHeader>

        <CardContent className="p-4 space-y-4">
          {/* WHY THIS SIGNAL */}
          {why.length > 0 && (
            <section
              className="border-l-4 border-[hsl(var(--success))]/60 pl-3"
              data-testid="rationale-why"
            >
              <h4 className="font-display text-sm font-semibold mb-2 flex items-center gap-1.5">
                <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                Why this signal
              </h4>
              <ul className="space-y-1.5">
                {why.map((p, i) => (
                  <li key={i} className="text-sm leading-relaxed flex gap-2">
                    <span className="text-[hsl(var(--success))] shrink-0 select-none">✅</span>
                    <span>{stripBullet(p)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* CONCERNS */}
          {concerns.length > 0 && (
            <section
              className="border-l-4 border-[hsl(var(--warning))]/60 pl-3"
              data-testid="rationale-concerns"
            >
              <h4 className="font-display text-sm font-semibold mb-2 flex items-center gap-1.5">
                <AlertTriangle className="h-3.5 w-3.5 text-warning" />
                Concerns to consider
              </h4>
              <ul className="space-y-1.5">
                {concerns.map((p, i) => (
                  <li key={i} className="text-sm leading-relaxed flex gap-2">
                    <span className="text-warning shrink-0 select-none">⚠</span>
                    <span>{stripBullet(p)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* WHAT TO WATCH */}
          {watch.length > 0 && (
            <section
              className="border-l-4 border-primary/60 pl-3"
              data-testid="rationale-watch"
            >
              <h4 className="font-display text-sm font-semibold mb-2 flex items-center gap-1.5">
                <Info className="h-3.5 w-3.5 text-primary" />
                What to watch
              </h4>
              <ul className="space-y-1.5">
                {watch.map((p, i) => (
                  <li key={i} className="text-sm leading-relaxed flex gap-2">
                    <span className="text-primary shrink-0 select-none">•</span>
                    <span>{stripBullet(p)}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* BOTTOM LINE */}
          {bottomLine && (
            <div
              className="rounded-lg bg-muted/40 border p-3"
              data-testid="rationale-bottom-line"
            >
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1 flex items-center gap-1.5">
                ⚖️ Bottom line
              </div>
              <p className="text-sm leading-relaxed text-foreground/95">
                {bottomLine}
              </p>
            </div>
          )}

          {/* COMPACT QUICK-TAKE */}
          {compact && (
            <div
              className="rounded-full border bg-card px-3 py-1.5 text-[11px] text-muted-foreground inline-flex items-center gap-1.5"
              data-testid="rationale-compact-pill"
            >
              <span className="font-medium text-foreground">Quick take:</span>
              <span>{compact}</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvestmentRationalePanel;
